import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { Subarea } from 'src/app/data-models/area-models/subarea.model';
import { ProductType } from 'src/app/data-models/catch-table-models/product-type.model';
import { LandingSite } from 'src/app/data-models/landing-site.model';
import { CatchService } from 'src/app/services/reporting/catch.service';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { SpeciesData } from '../../data-models/catch-table-models/species-data.model';
import { TableRules } from '../../data-models/catch-table-models/table-rules.model';
import { OffloadReport } from '../../data-models/report-models/offload-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { OffloadTableDialogComponent } from '../../dialogs/offload-table-dialog/offload-table-dialog.component';
import { AuthenticationService } from '../../services/authentication.service';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { environment } from '../../../environments/environment';
import { cloneDeep } from 'lodash';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { EmailService } from 'src/app/services/reporting/email.service';
import { FieldChange, ReportDiffService } from 'src/app/services/reporting/report-diff.service';
import { numericFormatValidator } from 'src/app/data-models/validator.model';


@Component({
    selector: 'app-offload-report',
    templateUrl: './offload-report.component.html',
    styleUrls: ['./offload-report.component.css'],
    standalone: false
})
export class OffloadReportComponent implements OnInit {

  isProduction: boolean = environment.production;

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  isEditing = false;
  private originalOffloadReport!: OffloadReport;
  private originalTableData!: SpeciesData[];

  // table data
  @ViewChild(MatTable) table!: MatTable<SpeciesData>; // for updating the table
  offloadTable: TableRules = new TableRules(); // used in add species dialog
  tableDataSource: SpeciesData[] = []; // all species data currently in the table
  displayedColumns: string[] = []; // columns currently being displayed in the table
  weightUnitOptions: string[] = ['Lbs', 'Kgs']; //hardcoded values to be used in the form

  // data preambles
  responseOptions: string[] = ['Yes', 'No']; //hardcoded values to be used in the form
  userDetails: UserData = new UserData();
  unfilteredFishList: SpeciesData[] = []; // temporary array to store species
  fishList: SpeciesData[] = []; // filtered list of species data, will comprise the user's look-up list
  productTypes: ProductType[] = []; // product type options to be presented to the user
  managementAreas: ManagementArea[] = []; // list of all management areas within the current trip's license area
  subAreas: Subarea[] = []; // list of all subareas within the current trip's management area
  currTrip: Trip = new Trip();
  offloadReport!: OffloadReport;
  tripReportIndex!: number; //Index of report in the current trip
  relevantLandingSites: LandingSite[] = [];
  dateError: string = 'Required';

  reportStatus: string = '';
  isComplete: boolean = false;
  reportFailed: boolean = false;

  offloadForm: UntypedFormGroup = new UntypedFormGroup({
    landingSite: new FormControl<string>('', [Validators.required]),
    productType: new FormControl<string>('', [Validators.required]), // page 1
    mgmtArea: new FormControl<string>('', [Validators.required]), // page 2
    subarea: new FormControl<string>('', [Validators.required]), // page 2 (conditionally validated)
    numDaysFished: new FormControl<string>('', [Validators.required, numericFormatValidator()]), // page 2 
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    fishSlipNumber: new FormControl<string>(''), // page 3 (optional)
    weightUnits: new FormControl<string>(''), // page 3 (always auto-selected so no need for validation)
    comments: new FormControl<string>('') // page 4 (optional)
  });

  offloadDate: Date = new Date(); // page 1
  minDate: Date = new Date();
  currDate: Date = new Date(); // max selectable offload date — offloads can't be in the future
  offloadTime: { hour: number, minute: number } = { hour: 0, minute: 0 }; // page 1
  futureDateTimeError: boolean = false; // true when the selected offload date + time is in the future

  formUpdateSubscription!: Subscription;


  // page controls
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;


  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private submissionService: SubmissionService,
    public sharedMethodsService: SharedMethodsService,
    public catchService: CatchService,
    public commonDataService: CommonDataService,
    private onlineStatusService: OnlineStatusService,
    private emailService: EmailService,
    
    
  
  ) {
    this.reportStatus = this.route.snapshot.queryParams.status;
    const nav = this.router.getCurrentNavigation()?.extras.state as any;
    if (this.reportStatus === 'post_submission_edit' && nav?.trip && nav?.report) {
      this.isEditing = true;
      this.currTrip = nav.trip;
      this.offloadReport = nav.report;
      this.tripReportIndex = this.currTrip.offloadReports.findIndex((report: OffloadReport) => report.internalId == this.offloadReport.internalId);
      // keep a deep clone for diffing later:
      this.originalOffloadReport = cloneDeep(this.offloadReport);
      // snapshot the original catch rows
      this.originalTableData = cloneDeep(this.offloadReport.speciesDataList);
      this.tableDataSource = cloneDeep(this.originalTableData);
      return;
    }
  }


  /*  ---------- Report Initialization Methods  ---------- */


  ngOnInit(): void {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['table_rules', 'species_list', 'product_types', 'landing_sites', 'management_areas_list', 'subareas_list'];
    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      if (this.isEditing) {
        this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => this.initialize());
        return;
      }
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        let reportId = <string>this.route.snapshot.paramMap.get('id');
        this.reportStatus = this.route.snapshot.queryParams.status;
        this.tripReportIndex = this.currTrip.offloadReports.findIndex((report: OffloadReport) => report.internalId == reportId);
        this.offloadReport = this.currTrip.offloadReports[this.tripReportIndex];
        this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
      });
    });
  }

  initialize(): void {

    if (this.route.snapshot.queryParams.status == 'new') {


      // fill the offload report with the end report data
      this.offloadTime = {
        hour: this.currTrip.endFishingReport.plannedOffloadDate.getHours(),
        minute: this.currTrip.endFishingReport.plannedOffloadDate.getMinutes()
      };
      this.offloadDate = this.currTrip.endFishingReport.plannedOffloadDate;

      if (this.offloadDate > this.currDate) {
        this.offloadDate = new Date();
        this.offloadTime = { hour: this.offloadDate.getHours(), minute: this.offloadDate.getMinutes() };
      }

      this.offloadForm.patchValue({ landingSite: this.currTrip.endFishingReport.landingSite });
      this.offloadForm.patchValue({ weightUnits: 'Lbs' }); // automatically set to lbs (most common unit)

    } else {
      this.patchForm();
      this.fillSubArea();
    }

    // get all required data
    this.unfilteredFishList = CommonDataService.speciesList;
    this.productTypes = CommonDataService.productTypes;
    this.relevantLandingSites = this.sharedMethodsService.getLandingSites(this.currTrip.licenseArea.licenseName, CommonDataService.landingSites);
    this.relevantLandingSites.sort((a,b) =>  0 - (a.name < b.name ? 1 : -1));
    this.managementAreas = this.sharedMethodsService.getManagementAreas(this.currTrip.licenseArea.licenseName, CommonDataService.managementAreas);

    this.setTableRules();
    this.filterSpeciesData(); // filter species list available to the user    
    this.setDisplayedColumns(); // initialize the table 

    this.subscribeToFormChanges();
    this.onLandingSiteChange();
    this.onManagementAreaChange();
    this.onDateRangeChange();

    if (!this.isEditing) {
      // set the management area and subarea from the last catch report
      const index = this.currTrip.dailyCatchReports.length - 1;
      this.offloadForm.controls['mgmtArea'].setValue(this.currTrip.dailyCatchReports[index].managementArea, { emitEvent: false });
      this.fillSubArea();
      this.offloadForm.controls['subarea'].setValue(this.currTrip.dailyCatchReports[index].subAreas[0], { emitEvent: false });

      // set the first and last day fished from the first and last catch reports
      this.offloadForm.controls['startDate'].setValue(this.currTrip.dailyCatchReports[0].dateFished, { emitEvent: false });
      this.offloadForm.controls['endDate'].setValue(this.currTrip.dailyCatchReports[index].dateFished);
    }

    this.numPages = 5;
    this.spinnerIncrement = 100 / this.numPages;
   
    //If the form is complete (uneditable) set it to view only summary screen
    if (this.reportStatus == 'complete') {

      if (SharedMethodsService.getReportStatus(this.offloadReport.confirmationNumber) == 'Error with submission') {
        this.reportFailed = true;
      }
      this.isComplete = true;
      this.currPage = 6;
      this.spinnerProgress = 100;
    } else if (this.isEditing) {
      // mark form pristine so only actual edits become dirty
      this.offloadForm.markAsPristine();
      this.toNextPage();
    } else {
      this.toNextPage();
    }

  }


  //Populates the form controls with values from the species at risk report
  patchForm() {

    this.offloadForm.patchValue({
      landingSite: this.offloadReport.landingSite,
      productType: this.offloadReport.productType,
      mgmtArea: this.offloadReport.lastMgmtArea,
      subarea: this.offloadReport.lastSubarea,
      numDaysFished: this.offloadReport.numDaysFished,
      startDate: this.offloadReport.firstDateFished,
      endDate: this.offloadReport.lastDateFished,
      fishSlipNumber: this.offloadReport.fishSlipNumber,
      weightUnits: this.offloadReport.catchWeightUnits,
      comments: this.offloadReport.comments
    });
    this.offloadDate = this.offloadReport.offloadDate;
    this.offloadTime = { hour: this.offloadReport.offloadDate.getHours(), minute: this.offloadReport.offloadDate.getMinutes() };

    this.offloadReport.speciesDataList.forEach((species: SpeciesData) => {
      let ind = this.tableDataSource.findIndex((sd: SpeciesData) => sd.speciesCode == species.speciesCode);
      if (ind == -1) {
        this.tableDataSource.push(species);
      } else
        this.tableDataSource[ind] = species;
    });

  }


  // Reopens the report, only possible if a submitted report gets a failure response from FOS
  reopenReport() {

    this.currPage = 1;
    this.spinnerProgress = this.spinnerIncrement;
    this.isComplete = false;
    this.reportFailed = false;

  }


  // Subscribe to all changes in the form to update and save as changes occur
  private subscribeToFormChanges(): void {

    if (this.isEditing) return;

    this.formUpdateSubscription = this.offloadForm.valueChanges.pipe(distinctUntilChanged()).subscribe(form => {
      const offloadReport = this.currTrip.offloadReports[this.tripReportIndex];
      if (offloadReport.lastDateFished && !form.endDate) return;

      offloadReport.landingSite = LandingSite.transformAnonymous(this.offloadForm.get('landingSite')?.value);
      offloadReport.productType = ProductType.transformAnonymous(this.offloadForm.get('productType')?.value);
      offloadReport.lastMgmtArea = ManagementArea.transformAnonymous(this.offloadForm.get('mgmtArea')?.value);
      offloadReport.lastSubarea = Subarea.transformAnonymous(this.offloadForm.get('subarea')?.value);
      offloadReport.numDaysFished = form.numDaysFished;
      offloadReport.firstDateFished = form.startDate;
      offloadReport.lastDateFished = form.endDate;
      offloadReport.fishSlipNumber = form.fishSlipNumber;
      offloadReport.catchWeightUnits = form.weightUnits;
      offloadReport.comments = form.comments;
      offloadReport.lastUpdate = new Date();
      UserStorageService.saveTrip(this.currTrip, true, false);
    });
  }


  dateTimeChange() {
    this.updateFutureDateTimeError();
    if (this.isEditing) return;
    this.currTrip.offloadReports[this.tripReportIndex].offloadDate = this.offloadDate;
    this.currTrip.offloadReports[this.tripReportIndex].offloadDate.setMinutes(this.offloadTime.minute);
    this.currTrip.offloadReports[this.tripReportIndex].offloadDate.setHours(this.offloadTime.hour);
    this.currTrip.offloadReports[this.tripReportIndex].lastUpdate = new Date();
    UserStorageService.saveTrip(this.currTrip, true, false);
  }

  // returns the offload date combined with the selected time (without mutating offloadDate)
  private getCombinedOffloadDateTime(): Date {
    const combined: Date = new Date(this.offloadDate);
    combined.setHours(this.offloadTime.hour, this.offloadTime.minute, 0, 0);
    return combined;
  }

  // the calendar's maxDate blocks future dates, but the separate time picker can still push
  // today's selection past the current time — flag when the combined date + time is in the future
  private updateFutureDateTimeError(): void {
    this.futureDateTimeError = this.getCombinedOffloadDateTime().getTime() > new Date().getTime();
  }


  // determines what time to display as a placeholder in the time picker
  setTime(): void {

    let tempDate: Date = new Date();

    if (tempDate.setHours(0, 0, 0, 0) === this.offloadDate.setHours(0, 0, 0, 0)) { // if the calendar date is the same as the current date
      this.offloadTime = { // set the time to the current time
        hour: new Date().getHours(),
        minute: new Date().getMinutes()
      }
    } // otherwise, the leave the time as zero

  }

  // sets the date error string to the appropriate message based on whether the dates are valid
  setDateError(dateValue1: any, dateValue2: any): void {

    if (dateValue1 && dateValue2) {
      this.dateError = 'Invalid Date Range';
    } else {
      this.dateError = 'Required';
    }

  }

  // update the date range error string if either the start or end date values change
  onDateRangeChange(): void {

    this.offloadForm.get('startDate')?.valueChanges.subscribe((startDate: Date | null) => {
      const endDate = this.offloadForm.get('endDate')?.value;
      this.setDateError(startDate, endDate);
      this.calculateAndSetNumDays(startDate, endDate);
    });

    this.offloadForm.get('endDate')?.valueChanges.subscribe((endDate: Date | null) => {
      const startDate = this.offloadForm.get('startDate')?.value;
      this.setDateError(endDate, startDate);
      this.calculateAndSetNumDays(startDate, endDate);
    });

  }


  // Calculate the number of days between start and end dates
  private calculateAndSetNumDays(startDate: Date | null, endDate: Date | null): void {
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // normalize to midnight local time
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const msPerDay = 1000 * 60 * 60 * 24;
      const diffDays = Math.floor((end.getTime() - start.getTime()) / msPerDay);

      // inclusive count → add 1 day
      const numDays = diffDays + 1;

      this.offloadForm.get('numDaysFished')!.setValue(numDays, { emitEvent: false });
    }

  }


  onManagementAreaChange(): void {

    this.offloadForm.get('mgmtArea')?.valueChanges.subscribe((value: string) => {
      let newManagementArea: ManagementArea = ManagementArea.transformAnonymous(value); // get new management area value from the form
      this.subAreas = this.sharedMethodsService.getSubareas(newManagementArea.name, true, CommonDataService.subareas); // update the subareas list based on the new management area
      this.offloadForm.controls['subarea'].setValue('', {emitEvent: false}); // reset subarea form value
      this.updateSubareaValidation();
    });

  }


  onLandingSiteChange(): void {
    this.offloadForm.get('landingSite')?.valueChanges.subscribe((value: string) => {
      const control = this.offloadForm.get('comments')!;
      if (value == 'Not Listed') control.addValidators(Validators.required);
      else control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    })
  }

  updateSubareaValidation(): void {

    const subareaControl: AbstractControl<any, any> | null = this.offloadForm.get('subarea');

    if (this.subAreas.length) {
      subareaControl?.setValidators([Validators.required]);
    } else {
      subareaControl?.clearValidators();
    }

    subareaControl?.updateValueAndValidity({ emitEvent: false });
    
  }

  fillSubArea(): void {

    let mgmtArea = this.offloadForm.get('mgmtArea')?.value;
    let newManagementArea: ManagementArea = ManagementArea.transformAnonymous(mgmtArea); // get new management area value from the form
    this.subAreas = this.sharedMethodsService.getSubareas(newManagementArea.name, false, CommonDataService.subareas); // update the subareas list based on the new management area
    this.updateSubareaValidation();

  }


  compareArea(a1: ManagementArea | Subarea, a2: ManagementArea | Subarea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }


  compareWith(a1: ProductType | LandingSite, a2: ProductType | LandingSite): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.code === a2.code : a1 === a2;
  }


  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the previous one
  toNextPage(): void {

    if (this.currPage <= this.numPages) {
      // set time if navigating to first page
      if (this.currPage === 1) {
        this.sharedMethodsService.setDateTime(this.offloadDate, this.offloadTime);
        // offloads cannot be in the future — block navigation if the date + time is ahead of now
        this.updateFutureDateTimeError();
        if (this.futureDateTimeError) {
          this.sharedMethodsService.toastFutureDateTime();
          return;
        }
      }
      // check for empty catch data if leaving catch table page
      if (this.currPage === 4 && this.catchService.isCatchEmpty('offload', this.tableDataSource)) {
        this.sharedMethodsService.toastNoCatch(true);
        return; // do not increment page if catch table is empty - catch validation
      }
      this.incrementPage();
      return;
    }
    if (this.isEditing) {
      this.submitEdits();
    } else {
      this.submitReport();
    }
  }

  // forward one page
  incrementPage(): void {

    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;

  }

  // check current page before going to the previous one
  toPreviousPage(): void {

    if (this.isComplete || this.currPage <= 1) {
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
    }
    this.decrementPage();

  }

  // back one page
  decrementPage(): void {

    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;

  }


  // Navigate to the respective page based on the field that is required
  navigateToPage(page: number) {
    this.spinnerProgress = page*this.spinnerIncrement;
    this.currPage = page;
  }


  /* ---------- Offload Table Methods ---------- */


  offloadTableDialog(): void {

    const dialogRef: MatDialogRef<OffloadTableDialogComponent, any> = this.dialog.open(OffloadTableDialogComponent, {
      width: '500px',
      height: '550px',
      data: { catchReports: this.currTrip.dailyCatchReports, tableData: this.tableDataSource }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.tableDataSource = result;
        this.offloadReport.speciesDataList = this.catchService.determineSpeciesData(this.tableDataSource);
        if (!this.isEditing) UserStorageService.saveTrip(this.currTrip, true, false);
        if (!this.tableDataSource.length) { // if no results returned
          this.sharedMethodsService.toastNoCatchMatch();
        }
        this.table.renderRows();
      }
    });

  }


  updateSpeciesTable(index: number) {
    // when editing (index > -1) re-add the currently edited species so it reappears in the picker;
    // when adding (index === -1) there's no row to re-add — concatenating tableDataSource[-1] would
    // push `undefined` into the list and break the dialog's species fill.
    const filteredFishList = index > -1 ? this.fishList.concat(this.tableDataSource[index]) : this.fishList;
    this.catchService.addSpeciesDialog(true, this.tableDataSource, filteredFishList, this.offloadTable, this.table, index).then(() => {
      this.currTrip.offloadReports[this.tripReportIndex].speciesDataList = this.catchService.determineSpeciesData(this.tableDataSource);
      if(!this.isEditing) UserStorageService.saveTrip(this.currTrip, true, false);
    });   
  }

  // table rules will be the same as they are for the current gear's Daily Catch Report with a couple differences:
  // tableType will instead be 'offload-catch' and containsSpecies list will be cleared since we are getting that info from the daily catch reports
  setTableRules(): void {

    switch (this.currTrip.logbook.gearName) {
      case 'Gill Net':
        this.offloadTable = CommonDataService.gillNetTableRules;
        break;
      case 'Seine':
        this.offloadTable = CommonDataService.seineTableRules;
        break;
      case 'Troll':
        this.offloadTable = CommonDataService.trollTableRules;
        break;
    }
    this.offloadTable.tableType = 'offload-catch';
    this.offloadTable.containsSpecies.length = 0; // clear contains list

  }

  setDisplayedColumns(): void {

    if (this.getCurrentGearType() === 'Seine') {
      this.displayedColumns = this.catchService.seineOffloadColumns;
      return;
    }
    // will be Gill Net in this case
    this.displayedColumns = this.catchService.gillNetOffloadColumns;

  }



  filterSpeciesData(): void {  // filters the species list accessible to the user in the add species dialog

    for (let species of this.unfilteredFishList) {
      let excludeFlag: boolean = false;
      for (let tableSpecies of this.tableDataSource) {
        if (species.speciesName === tableSpecies.speciesName) {
          excludeFlag = true; // we only want to add species to the list that are not already in the table
        }
      }
      if (!excludeFlag) { // if the species is not already in the table
        if (!this.offloadTable.excludesSpecies.includes(species.speciesName) && // only add the species if it is not excluded from in the gear type
          !this.offloadTable.releaseOnlySpecies.includes(species.speciesName)) { // only add the species if it is not release-only
          this.fishList.push(species);
        }
      }
      excludeFlag = false; // reset flag
    }

  }


  /* ---------- Data Retrieval Methods ---------- */



  getWeightUnits(): string {

    return this.offloadForm.get('weightUnits')?.value.trim();

  }

  getCurrentGearType(): string {

    return this.currTrip.logbook.gearName;

  }


  /* ---------- Report Completion Methods ---------- */


// submits the report edits, collects diffs, and sends the email to Archipelago
  submitEdits(): void {
  
    if (this.onlineStatusService.getStatus() !== OnlineStatusType.ONLINE) {
      this.sharedMethodsService.toastOfflineError();
      return;
    }
    this.isReportSending = true;
    // first write user’s edits into the model
    this.saveReportData();

    const changes: FieldChange[] = this.getChangedFields();

    const isActiveTrip = this.currTrip.tripID === UserStorageService.currTrip.tripID;
    UserStorageService.saveTrip(this.currTrip, isActiveTrip, true).then(() => {
      // send an email to archipelago with the fisher's info, including the changed fields
      const payload = {
        fisherName: this.userDetails.firstName + ' ' + this.userDetails.lastName,
        fin: this.currTrip.fin,
        vesselName: this.currTrip.vessel.vesselName,
        vrn: this.currTrip.vessel.vesselRegistrationNumber,
        logbook: this.currTrip.logbook.logbookID,
        tripId: this.currTrip.tripID,
        reportId: this.offloadReport.reportID,
        changedFields: changes,
      };

      this.emailService.sendEditNotification(payload).subscribe(
        () => console.log("Edit‐notification email queued"),
        err => console.error("Failed to queue edit email", err)
      );
      this.router.navigate(['submission-page'], { state: { isEditSubmission: true } });
    });
  }


  // every field of the report a fisher can reach while editing, compared against the submitted version
  private getChangedFields(): FieldChange[] {

    const changed: FieldChange[] = [];
    const original: OffloadReport = this.originalOffloadReport;
    const updated: OffloadReport = this.offloadReport;

    ReportDiffService.add(changed, 'offloadDate', 'Offload Date', original.offloadDate, updated.offloadDate, ReportDiffService.dateTime);
    ReportDiffService.add(changed, 'landingSite', 'Landing Site', original.landingSite, updated.landingSite);
    ReportDiffService.add(changed, 'productType', 'Product Type', original.productType, updated.productType);
    ReportDiffService.add(changed, 'lastMgmtArea', 'Last Management Area', original.lastMgmtArea, updated.lastMgmtArea);
    ReportDiffService.add(changed, 'lastSubarea', 'Last Subarea Fished', original.lastSubarea, updated.lastSubarea);
    ReportDiffService.add(changed, 'firstDateFished', 'First Date Fished', original.firstDateFished, updated.firstDateFished, ReportDiffService.date);
    ReportDiffService.add(changed, 'lastDateFished', 'Last Date Fished', original.lastDateFished, updated.lastDateFished, ReportDiffService.date);
    ReportDiffService.add(changed, 'numDaysFished', 'Number of Days Fished', original.numDaysFished, updated.numDaysFished);
    ReportDiffService.add(changed, 'fishSlipNumber', 'Fish Slip Number', original.fishSlipNumber, updated.fishSlipNumber);
    ReportDiffService.add(changed, 'catchWeightUnits', 'Catch Weight Units', original.catchWeightUnits, updated.catchWeightUnits);
    ReportDiffService.addCatchTable(changed, 'speciesDataList', 'Offload Table', this.originalTableData, updated.speciesDataList, ReportDiffService.offloadColumns);
    ReportDiffService.add(changed, 'comments', 'Comments', original.comments, updated.comments);

    return changed;

  }


  // verifies that all required report fields are complete, then initiates the saving/sending process
  submitReport(): void {

    if (this.offloadForm.valid) {
      this.isReportSending = true;
      this.saveReportData();
      this.attemptTransmission(); // attempt to send the report to DFO
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }

  // saves report data using the form data collected
  saveReportData(): void {

    // copy relevant data to offload report object
    this.offloadReport.offloadDate = this.offloadDate;
    this.offloadReport.landingSite = LandingSite.transformAnonymous(this.offloadForm.get('landingSite')?.value)
    this.offloadReport.productType = ProductType.transformAnonymous(this.offloadForm.get('productType')?.value);
    this.offloadReport.lastMgmtArea = ManagementArea.transformAnonymous(this.offloadForm.get('mgmtArea')?.value);
    this.offloadReport.lastSubarea = Subarea.transformAnonymous(this.offloadForm.get('subarea')?.value);
    this.offloadReport.numDaysFished = this.offloadForm.get('numDaysFished')?.value;
    this.offloadReport.firstDateFished = new Date(this.offloadForm.get('startDate')?.value);
    this.offloadReport.firstDateFished.setHours(12, 0, 0, 0); // Set time to noon since the time isn't part of the submitted date
    this.offloadReport.lastDateFished = new Date(this.offloadForm.get('endDate')?.value);
    this.offloadReport.lastDateFished.setHours(12, 0, 0, 0); // Set time to noon since the time isn't part of the submitted date
    this.offloadReport.fishSlipNumber = this.offloadForm.get('fishSlipNumber')?.value;
    this.offloadReport.speciesDataList = this.catchService.determineSpeciesData(this.tableDataSource);
    this.offloadReport.comments = this.offloadForm.get('comments')?.value.trim();
    this.offloadReport.catchWeightUnits = this.offloadForm.get('weightUnits')?.value.trim();
    this.offloadReport.completionDate = new Date(); // get current date/time
    this.offloadReport.submissionDate = new Date();
    this.offloadReport.isComplete = true;


  }



  // composes the email body and subject, then invokes a method to email the report to DFO
  attemptTransmission(): void {
    // A reopened report still carries the old FOS error text; clear it before the pre-send save,
    // otherwise the saveTrip merge sees matching completionDates and restores the stale error
    // over the resubmission (ties keep the error).
    if (SharedMethodsService.getReportStatus(this.offloadReport.confirmationNumber) === 'Error with submission')
      this.offloadReport.confirmationNumber = 'Awaiting response';
    UserStorageService.saveUser(this.userDetails).then(() => {
      UserStorageService.saveTrip(this.currTrip).then(() => {
        // change false to !isProduction to enable mocking the submission
        if (false) { // mock the submission process if the app is in development mode
          this.sharedMethodsService.toastEmailSent();
          this.offloadReport.confirmationNumber = "FOS 12345";
          this.offloadReport.isSubmitted = true;
          UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Offload Catch Report', success: this.offloadReport.isSubmitted }, replaceUrl: true }));
        } else {
          this.submissionService.sendReportData(this.userDetails.userEmail, this.offloadReport.reportID, this.currTrip.tripID).then((success: boolean) => {
            if (success) { // if the email was sent successfully
              this.sharedMethodsService.toastEmailSent();
              this.offloadReport.isSubmitted = true; // set the report submissionflag
              this.offloadReport.confirmationNumber = "Awaiting response";
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Offload Catch Report', success: this.offloadReport.isSubmitted }, replaceUrl: true }));
            } else {
              this.offloadReport.confirmationNumber = "Not submitted";
              this.sharedMethodsService.toastUnableToSubmitReport
              this.currTrip.unsubmittedReports.push(this.offloadReport.completionDate);
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'Offload Catch Report', success: this.offloadReport.isSubmitted }, replaceUrl: true }));
            }
          });
        }
      });
    });
    

  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'delete_report' }
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.offloadReports, this.offloadReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }
    });
  }

  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }

}
